import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDateLocale } from "../../hooks/useDateLocale";
import type { IColor } from "../../types";
import type { IsoDate } from "../../utils/date";
import {
  clamp,
  FIRST_DAY,
  firstOfMonth,
  LAST_DAY,
  monthOf,
  todayIso,
} from "../../utils/date";
import type { ICalendarContext, ICalendarProps } from "./types";
import { CalendarView } from "./types";

/** The value when it can be picked, else today moved into the range. */
const openingDay = (
  value: IsoDate | null,
  today: IsoDate,
  first: IsoDate,
  last: IsoDate,
): IsoDate =>
  value !== null && value >= first && value <= last
    ? value
    : clamp(today, first, last);

export const useViewModel = (props: ICalendarProps): ICalendarContext => {
  const { value, onChange, markers, autoFocus, onMonthChange } = props;
  const locale = useDateLocale();
  const today = props.today ?? todayIso();
  const firstDay = props.firstDay ?? locale.firstDay;
  const first = props.min ?? FIRST_DAY;
  const last = props.max ?? LAST_DAY;

  const [cursor, setCursor] = useState(() =>
    openingDay(value, today, first, last),
  );
  const [view, setView] = useState(CalendarView.Days);
  const focusRequest = useRef(!!autoFocus);
  const rootRef = useRef<HTMLDivElement>(null);

  /**
   * A cell about to unmount (another month, another view) would drop focus to
   * <body>, and a popup around the calendar would close. The root takes it
   * until the grid's effect puts it on the new cell.
   */
  const holdFocus = useCallback(() => {
    const root = rootRef.current;
    if (
      root &&
      root !== document.activeElement &&
      root.contains(document.activeElement)
    ) {
      root.focus({ preventScroll: true });
    }
  }, []);

  // A new value or range reopens the days on it (derived during render, no effect pass).
  const openingKey = `${value}|${first}|${last}`;
  const [lastOpeningKey, setLastOpeningKey] = useState(openingKey);
  if (openingKey !== lastOpeningKey) {
    setLastOpeningKey(openingKey);
    setCursor(openingDay(value, today, first, last));
    setView(CalendarView.Days);
  }

  const moveTo = useCallback(
    (date: IsoDate | null, focus: boolean) => {
      if (date === null) return;
      if (focus) holdFocus();
      focusRequest.current = focus;
      setCursor(clamp(date, first, last));
    },
    [first, last, holdFocus],
  );

  const showView = useCallback(
    (next: CalendarView, focus: boolean) => {
      if (focus) holdFocus();
      focusRequest.current = focus;
      setView(next);
    },
    [holdFocus],
  );

  const pick = useCallback(
    (date: IsoDate) => {
      if (date < first || date > last) return;
      onChange(date);
    },
    [first, last, onChange],
  );

  const shownMonth = firstOfMonth(monthOf(cursor));
  useEffect(() => {
    onMonthChange?.(shownMonth);
  }, [shownMonth, onMonthChange]);

  const markersMap = useMemo(() => {
    const map: Record<IsoDate, IColor> = {};
    for (const marker of markers ?? []) map[marker.date] = marker.color;
    return map;
  }, [markers]);

  return useMemo(
    () => ({
      value,
      today,
      first,
      last,
      firstDay,
      locale,
      markers: markersMap,
      cursor,
      view,
      moveTo,
      showView,
      pick,
      focusRequest,
      rootRef,
    }),
    [
      value,
      today,
      first,
      last,
      firstDay,
      locale,
      markersMap,
      cursor,
      view,
      moveTo,
      showView,
      pick,
    ],
  );
};
